import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Watch, Link2, Unlink, RefreshCw, AlertTriangle, ExternalLink, Copy } from 'lucide-react';
import { useAuthStore } from '../../store/useStore';
import GraficoLongevity from './GraficoLongevity';
import { polarAPI } from '../../services/polarApi';
import '../../styles/Longevity.css';
import '../../styles/LongevityConsole.css';

/**
 * LongevityTab — i dati del braccialetto Polar del paziente.
 *
 * NIENTE VALORI FINTI, MAI
 * Questo riquadro ha vissuto mesi con dati dimostrativi, e andava bene
 * finche' c'era scritto a caratteri cubitali che erano inventati. Adesso
 * che i dati veri possono arrivare, i finti non possono restare nemmeno
 * come segnaposto: una frequenza cardiaca notturna plausibile accanto al
 * nome di un paziente e' qualcosa che qualcuno prima o poi leggera' come
 * vera. Se non c'e' un valore non si disegna la scheda.
 *
 * I TRE STATI CHE NON SONO LO STESSO STATO
 * "Nessun braccialetto collegato", "collegato ma non ha ancora mandato
 * niente" e "collegato, ha mandato dati" sono situazioni diverse e
 * vogliono tre schermate diverse. Quella di mezzo e' la piu' facile da
 * sbagliare: un riquadro vuoto senza spiegazione fa pensare a un guasto,
 * quando invece va solo aspettato che il braccialetto sincronizzi.
 */

const dataIta = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('it-IT');
};

const dataOraIta = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
};

const oraIta = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d) ? null : d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
};

/* Il null si scarta PRIMA di Number(), perche' Number(null) e Number('')
   valgono zero. Con la vecchia versione un campo che Polar non aveva
   ancora mandato compariva come "0,0": un valore inventato accanto al nome
   di un paziente, cioe' esattamente cio' che questo riquadro non deve fare.
   Successo davvero con il recupero notturno, che Polar comincia a calcolare
   solo dopo qualche notte di linea di base. */
const num = (v, decimali = 0) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n)
        ? n.toLocaleString('it-IT', { minimumFractionDigits: decimali, maximumFractionDigits: decimali })
        : null;
};

/** Secondi in "7h 20m". Null se non c'e' niente da mostrare. */
const durata = (secondi) => {
    const s = Number(secondi);
    if (!Number.isFinite(s) || s <= 0) return null;
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};

/* Lo stato del recupero notturno arriva da Polar come numero da 1 a 6.
   La parola la mettiamo noi: un "3" da solo, in una cartella clinica, non
   dice niente a chi legge. */
const PAROLA_STATO = {
    1: 'molto basso', 2: 'basso', 3: 'nella media',
    4: 'buono', 5: 'ottimo', 6: 'molto alto',
};
const parolaStato = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    if (Number.isFinite(n) && PAROLA_STATO[n]) return PAROLA_STATO[n];
    // Se un giorno Polar mandasse una parola invece di un numero, si mostra
    // quella invece di buttarla via.
    return Number.isFinite(n) ? null : String(v);
};

/** Scheda con un numero. Non si disegna se il numero non c'e'. */
const Scheda = ({ titolo, valore, unita, nota }) => {
    if (valore === null || valore === undefined) return null;
    return (
        <div className="lg-card">
            <div className="lg-card-titolo">{titolo}</div>
            <div className="lg-valore-grande">
                {valore}{unita && <span className="lg-unita"> {unita}</span>}
            </div>
            {nota && <div className="lg-card-nota">{nota}</div>}
        </div>
    );
};

/* ------------------------------------------------------------------ */

const LongevityTab = ({ pazienteId }) => {
    const utente = useAuthStore((s) => s.user);
    const gestisce = ['admin', 'segreteria'].includes(utente?.ruolo);

    const [dati, setDati] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [messaggio, setMessaggio] = useState(null);
    const [inCorso, setInCorso] = useState(false);
    const [collegamento, setCollegamento] = useState(null);   // { link }
    const [confermaScollega, setConfermaScollega] = useState(false);

    const attesa = useRef(null);

    const carica = useCallback(async (silenzioso = false) => {
        try {
            if (!silenzioso) setCaricamento(true);
            const r = await polarAPI.stato(pazienteId);
            setDati(r.data.data);
            setErrore(null);
            return r.data.data;
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Non riesco a leggere i dati del braccialetto');
            return null;
        } finally {
            if (!silenzioso) setCaricamento(false);
        }
    }, [pazienteId]);

    useEffect(() => { carica(); }, [carica]);

    // L'attesa del ritorno da Polar si ferma quando si cambia schermata
    useEffect(() => () => { if (attesa.current) clearInterval(attesa.current); }, []);

    /* Il paziente autorizza in un'altra finestra e Polar non ci avvisa:
       finita l'autorizzazione, il nostro server ha la riga in tabella ma
       questa pagina non lo sa. Si richiede lo stato ogni tre secondi
       finche' non compare, per due minuti - poi si smette, perche' un
       polling che non finisce mai resta acceso tutta la giornata. */
    const aspettaCollegamento = () => {
        if (attesa.current) clearInterval(attesa.current);
        const scade = Date.now() + 120000;
        attesa.current = setInterval(async () => {
            const d = await carica(true);
            if (d?.collegato) {
                clearInterval(attesa.current);
                attesa.current = null;
                setCollegamento(null);
                setMessaggio('Braccialetto collegato.');
            } else if (Date.now() > scade) {
                clearInterval(attesa.current);
                attesa.current = null;
            }
        }, 3000);
    };

    const avviaCollegamento = async () => {
        try {
            setInCorso(true);
            setErrore(null);
            setMessaggio(null);
            const r = await polarAPI.link(pazienteId);
            const link = r.data.data.link;
            setCollegamento({ link });
            window.open(link, 'polar', 'width=520,height=760');
            aspettaCollegamento();
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Impossibile avviare il collegamento');
        } finally {
            setInCorso(false);
        }
    };

    const aggiorna = async () => {
        try {
            setInCorso(true);
            setErrore(null);
            const r = await polarAPI.sincronizza(pazienteId);
            setMessaggio(r.data.message);
            await carica(true);
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Aggiornamento non riuscito');
            await carica(true);
        } finally {
            setInCorso(false);
        }
    };

    const scollega = async () => {
        try {
            setInCorso(true);
            setErrore(null);
            const r = await polarAPI.scollega(pazienteId);
            setMessaggio(r.data.message);
            setConfermaScollega(false);
            await carica(true);
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Impossibile scollegare');
        } finally {
            setInCorso(false);
        }
    };

    if (caricamento) return <p className="lg-attesa">Caricamento…</p>;

    if (errore && !dati) {
        return (
            <div className="lg-wrap">
                <p className="lg-errore"><AlertTriangle size={15} /> {errore}</p>
            </div>
        );
    }

    /* ---------------- non collegato ---------------- */
    if (!dati?.collegato) {
        return (
            <div className="lg-wrap">
                <div className="lg-testa">
                    <h2>Longevity</h2>
                </div>

                <div className="lg-vuoto">
                    <Watch size={34} strokeWidth={1.4} />
                    <p><strong>Nessun braccialetto collegato a questa scheda.</strong></p>
                    {gestisce ? (
                        <>
                            <p className="lg-vuoto-nota">
                                Il collegamento si fa con il paziente presente: si apre
                                l&apos;autorizzazione Polar, il paziente entra nel proprio
                                account e concede l&apos;accesso. Da noi restano solo
                                l&apos;identificativo e il permesso di leggere &mdash; non i suoi
                                dati di profilo.
                            </p>
                            <button className="lg-btn lg-btn--principale"
                                    onClick={avviaCollegamento} disabled={inCorso}>
                                <Link2 size={15} /> Collega un account Polar
                            </button>
                        </>
                    ) : (
                        <p className="lg-vuoto-nota">
                            Lo collega la segreteria, con il paziente presente.
                        </p>
                    )}
                </div>

                {collegamento && (
                    <div className="lg-collegamento">
                        <p>
                            Si è aperta la finestra di Polar. Se non compare, o se il
                            paziente preferisce usare il proprio telefono, questo è
                            l&apos;indirizzo da aprire &mdash; vale venti minuti:
                        </p>
                        <div className="lg-link-riga">
                            <input readOnly value={collegamento.link}
                                   onFocus={(e) => e.target.select()} />
                            <button className="lg-btn"
                                    onClick={() => navigator.clipboard?.writeText(collegamento.link)}>
                                <Copy size={14} /> Copia
                            </button>
                            <a className="lg-btn" href={collegamento.link}
                               target="_blank" rel="noreferrer">
                                <ExternalLink size={14} /> Apri
                            </a>
                        </div>
                        <p className="lg-vuoto-nota">
                            Appena il paziente conferma, questo riquadro si aggiorna da solo.
                        </p>
                    </div>
                )}

                {errore && <p className="lg-errore"><AlertTriangle size={15} /> {errore}</p>}
                {messaggio && <p className="lg-messaggio">{messaggio}</p>}
            </div>
        );
    }

    /* ---------------- collegato ---------------- */
    const recuperi = dati.recuperi || [];
    const sonni = dati.sonno || [];
    const ultimo = recuperi[0] || null;
    const ultimaNotte = sonni[0] || null;
    const senzaDati = !recuperi.length && !sonni.length;

    const totaleSonno = ultimaNotte
        ? (Number(ultimaNotte.sonno_leggero_s) || 0) + (Number(ultimaNotte.sonno_profondo_s) || 0)
          + (Number(ultimaNotte.sonno_rem_s) || 0) + (Number(ultimaNotte.sonno_ignoto_s) || 0)
        : 0;

    const fase = (etichetta, secondi, colore) => {
        const d = durata(secondi);
        if (!d) return null;
        return (
            <div className="lg-fase" key={etichetta}>
                <span className="lg-fase-barra"
                      style={{ background: colore, width: `${Math.min(100, (secondi / totaleSonno) * 100)}%` }} />
                <span className="lg-fase-label">{etichetta} {d}</span>
            </div>
        );
    };

    return (
        <div className="lg-wrap">
            <div className="lg-testa">
                <h2>Longevity</h2>
                <span className="lg-aggiornato">
                    Ultima lettura: {dataOraIta(dati.ultima_lettura_il)}
                </span>
            </div>

            {dati.token_revocato && (
                <div className="lg-banner lg-banner--allarme">
                    <AlertTriangle size={17} />
                    <div>
                        <strong>Il paziente ha revocato l&apos;accesso</strong> dal proprio
                        account Polar. Non arrivano più dati nuovi; quelli già raccolti
                        restano. Per riprendere va rifatto il collegamento.
                    </div>
                </div>
            )}

            <div className="lg-barra-azioni">
                <span className="lg-collegato-da">
                    Collegato il {dataIta(dati.collegato_il)}
                    {dati.collegato_da_username && ` da ${dati.collegato_da_username}`}
                </span>
                <div className="lg-azioni">
                    <button className="lg-btn" onClick={aggiorna} disabled={inCorso}>
                        <RefreshCw size={14} /> Aggiorna adesso
                    </button>
                    {gestisce && (
                        <button className="lg-btn lg-btn--pericolo"
                                onClick={() => setConfermaScollega(true)} disabled={inCorso}>
                            <Unlink size={14} /> Scollega
                        </button>
                    )}
                </div>
            </div>

            {confermaScollega && (
                <div className="lg-conferma">
                    <p>
                        <strong>Scollegare il braccialetto?</strong> L&apos;accesso viene
                        revocato anche presso Polar, quindi da lì in poi non arriva più
                        niente. Le notti già registrate restano nella cartella.
                    </p>
                    <div className="lg-azioni">
                        <button className="lg-btn" onClick={() => setConfermaScollega(false)}>
                            Annulla
                        </button>
                        <button className="lg-btn lg-btn--pericolo" onClick={scollega} disabled={inCorso}>
                            <Unlink size={14} /> Scollega e revoca
                        </button>
                    </div>
                </div>
            )}

            {errore && <p className="lg-errore"><AlertTriangle size={15} /> {errore}</p>}
            {messaggio && <p className="lg-messaggio">{messaggio}</p>}

            {senzaDati ? (
                <div className="lg-vuoto">
                    <Watch size={34} strokeWidth={1.4} />
                    <p><strong>Il braccialetto è collegato, ma non ha ancora inviato dati.</strong></p>
                    <p className="lg-vuoto-nota">
                        I valori compaiono qui dopo che il paziente ha indossato il
                        braccialetto per una notte e l&apos;ha sincronizzato con
                        l&apos;app Polar Flow. La lettura è automatica ogni mattina.
                    </p>
                    <p className="lg-vuoto-nota">
                        Il recupero notturno e la variabilità cardiaca li rilevano solo
                        i modelli con sensore ottico al polso: un braccialetto che conta
                        i passi resterà collegato senza mai riempire questo riquadro.
                    </p>
                </div>
            ) : (
                <>
                    {ultimo && (
                        <div className="lg-griglia lg-griglia-top">
                            <Scheda titolo="Variabilità cardiaca (HRV)"
                                    valore={num(ultimo.hrv_media)} unita="ms"
                                    nota={`Notte del ${dataIta(ultimo.giorno)}`} />
                            <Scheda titolo="Battito notturno"
                                    valore={num(ultimo.battito_medio)} unita="bpm"
                                    nota="Media della notte" />
                            <Scheda titolo="Respiro"
                                    valore={num(ultimo.respiro_medio, 1)} unita="atti/min"
                                    nota="Media della notte" />
                            {parolaStato(ultimo.stato) || num(ultimo.ans_carica, 1) ? (
                                <Scheda titolo="Recupero notturno (Polar)"
                                        valore={parolaStato(ultimo.stato) || num(ultimo.ans_carica, 1)}
                                        nota={parolaStato(ultimo.stato) && ultimo.ans_carica != null
                                              ? `Carica ANS ${num(ultimo.ans_carica, 1)}` : null} />
                            ) : (
                                /* Polar lo calcola contro la linea di base della
                                   persona: nelle prime notti non lo manda affatto,
                                   e i campi arrivano assenti. Dirlo e' l'unica cosa
                                   onesta - un numero al loro posto sarebbe inventato. */
                                <div className="lg-card lg-card--attesa">
                                    <div className="lg-card-titolo">Recupero notturno (Polar)</div>
                                    <div className="lg-card-attesa">non ancora calcolato</div>
                                    <div className="lg-card-nota">
                                        Polar lo produce confrontando la notte con la linea
                                        di base della persona: servono alcune notti prima
                                        che compaia.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {ultimaNotte && durata(totaleSonno) && (
                        <div className="lg-griglia lg-griglia-due">
                            <div className="lg-card lg-card-largo">
                                <div className="lg-card-titolo">
                                    Sonno — notte del {dataIta(ultimaNotte.giorno)}
                                </div>
                                <div className="lg-sonno-testa">
                                    <div className="lg-valore-grande">{durata(totaleSonno)}</div>
                                    {ultimaNotte.punteggio != null && (
                                        <div className="lg-sonno-qualita">
                                            Punteggio <strong>{num(ultimaNotte.punteggio)}</strong>
                                        </div>
                                    )}
                                </div>
                                {oraIta(ultimaNotte.inizio) && oraIta(ultimaNotte.fine) && (
                                    <div className="lg-card-nota">
                                        Dalle {oraIta(ultimaNotte.inizio)} alle {oraIta(ultimaNotte.fine)}
                                    </div>
                                )}
                                <div className="lg-fasi">
                                    {fase('Profondo', ultimaNotte.sonno_profondo_s, '#2b5f7a')}
                                    {fase('REM', ultimaNotte.sonno_rem_s, '#4a90b8')}
                                    {fase('Leggero', ultimaNotte.sonno_leggero_s, '#a8d0e0')}
                                    {fase('Non classificato', ultimaNotte.sonno_ignoto_s, '#cfd8dc')}
                                </div>
                                {durata(ultimaNotte.interruzioni_s) && (
                                    <div className="lg-card-nota">
                                        Interruzioni: {durata(ultimaNotte.interruzioni_s)}
                                    </div>
                                )}
                            </div>

                            <div className="lg-card lg-card-largo">
                                <div className="lg-card-titolo">Struttura della notte</div>
                                <div className="lg-card-nota">
                                    {sonni.length === 1
                                        ? 'Una notte registrata.'
                                        : `${sonni.length} notti di sonno registrate.`}
                                    {ultimaNotte.continuita != null
                                        && ` Continuità ${num(ultimaNotte.continuita, 1)} su 5.`}
                                </div>
                            </div>
                        </div>
                    )}

                    <GraficoLongevity recuperi={recuperi} />
                </>
            )}

            <div className="lg-piede">
                Fonte: Polar AccessLink. Della persona conserviamo l&apos;identificativo
                del suo account e il permesso di leggere: nome, data di nascita, sesso,
                peso e altezza che Polar restituisce non vengono salvati.
            </div>
        </div>
    );
};

export default LongevityTab;
